import { Fragment, useState } from "react";

import { Button } from "@bokio/designsystem/components/Button";
import Icon from "@bokio/designsystem/components/Icon/Icon";
import { Modal, ModalContent, ModalFooter } from "@bokio/designsystem/components/Modal";
import { Link } from "@bokio/elements/Link/Link";
import * as styles from "@bokio/elements/Sorter/sorterHeadingMobile.scss";
import GeneralLangFactory from "@bokio/lang/GeneralLangFactory";
import { mergeClassNames } from "@bokio/utils/classes";

import { useDeviceQuery } from "../DeviceQuery/useDeviceQuery";
import { MobileOverlay } from "../MobileOverlay/MobileOverlay";

import type { SorterConfig } from "./Sorter";

interface SorterHeadingMobileProps<T> {
	sortItems: SorterItemsMobileConfig[];
	config: SorterConfig<T>;
	onUpdate: (newConfig: SorterConfig<T>) => void;
	forceMobileLayout?: boolean;
}

export interface SorterItemsMobileConfig {
	sortKey: string;
	sortLabel: string;
	active: boolean;
}

const SorterHeadingMobile = <T,>(props: SorterHeadingMobileProps<T>) => {
	const [showSort, setShowSort] = useState(false);
	const { config, sortItems, onUpdate } = props;
	const lang = GeneralLangFactory();

	const deviceQuery = useDeviceQuery();
	const isMobile = deviceQuery.isMobile || props.forceMobileLayout === true;

	const activeItem = sortItems.filter(item => item.active)[0].sortLabel;

	const activeOrderIcon = config.sortOrder == "asc" ? "ascending" : "descending";

	const toggleSort = () => setShowSort(prevSort => !prevSort);

	const handleOrderChange = (order: "asc" | "desc") => {
		onUpdate({ ...config, sortOrder: order, sortProperty: config.sortProperty });

		if (deviceQuery.isMobile) {
			toggleSort();
		}
	};

	const handleSortChange = (sortKey: string) => {
		onUpdate({ ...config, sortOrder: config.sortOrder, sortProperty: sortKey });

		if (deviceQuery.isMobile) {
			toggleSort();
		}
	};

	if (!isMobile) {
		return null;
	}

	const renderFilter = () => {
		return (
			<>
				<div className={styles.sortGroup}>
					<p className={styles.title}>{lang.Order_label}</p>
					<Link
						style="none"
						className={mergeClassNames(styles.sortItem, config.sortOrder === "desc" && styles.active)}
						onClick={() => handleOrderChange("desc")}
					>
						{lang.Descending_label}
					</Link>
					<Link
						style="none"
						className={mergeClassNames(styles.sortItem, config.sortOrder === "asc" && styles.active)}
						onClick={() => handleOrderChange("asc")}
					>
						{lang.Ascending_label}
					</Link>
				</div>
				<div>
					<p className={styles.title}>{lang.SortBy}</p>
					{sortItems.map((item, i) => (
						<Link
							style="none"
							key={i}
							className={mergeClassNames(styles.sortItem, item.active && styles.active)}
							onClick={() => handleSortChange(item.sortKey)}
						>
							{item.sortLabel}
						</Link>
					))}
				</div>
			</>
		);
	};

	return (
		<Fragment>
			<Link style="none" className={styles.sortByLabel} onClick={toggleSort}>
				{lang.SortBy}: {activeItem} <Icon name={activeOrderIcon} />
			</Link>
			{deviceQuery.isMobile && (
				<MobileOverlay onClose={toggleSort} visible={showSort} heading={lang.Sort}>
					{renderFilter()}
				</MobileOverlay>
			)}
			{!deviceQuery.isMobile && (
				<Modal onClose={toggleSort} visible={showSort} title={lang.Sort}>
					<ModalContent>{renderFilter()}</ModalContent>
					<ModalFooter>
						<Button appearance="primary" onClick={toggleSort}>
							{lang.Close}
						</Button>
					</ModalFooter>
				</Modal>
			)}
		</Fragment>
	);
};

export default SorterHeadingMobile;
